import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, UserCheck, ShieldCheck, MapPin, Target as TargetIcon,
  Flag, TrendingUp, Plus, Vote, Award, Building2, Home, Landmark,
  AlertTriangle, RefreshCcw, Search, ChevronRight, Info, CheckCircle2,
  Edit2, Save, X
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { statsApi, partisipanApi, wilayahTargetApi } from '../lib/api';
import { formatNumber } from '../components/shared/UI';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { AuthContext } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import PetaDapilLeaflet from '../components/shared/PetaDapilLeaflet';

// --------------------------------------------------------------------------
// DATA PERENCANAAN STRATEGIS 2029 (DARI PPT: Target_Kursi_Kab_Sukabumi_2029.pptx)
// --------------------------------------------------------------------------
const STRATEGIC_PLAN = {
  wilayah: {
    kecamatan: 47,
    desa: 386,
    rw: 3228,
    dpt_2024: 1997822,
  },
  indikator: {
    target_kursi: 9,
    baseline_suara_2024: 161304,
    target_suara_2029: 256204,
    gap_suara: 94900,
  },
  dapil_table: [
    { dapil: 'Dapil 1', kursi_2029: 1, baseline: 15719, target: 21419, gap: 5700, kategori: 'Perjuangan' },
    { dapil: 'Dapil 2', kursi_2029: 1, baseline: 23392, target: 33492, gap: 10100, kategori: 'Perjuangan' },
    { dapil: 'Dapil 3', kursi_2029: 2, baseline: 29914, target: 58764, gap: 28850, kategori: 'Menantang', is_tambah: true, ambang: 50938 },
    { dapil: 'Dapil 4', kursi_2029: 3, baseline: 52788, target: 85338, gap: 32550, kategori: 'Potensial', is_tambah: true, ambang: 78106 },
    { dapil: 'Dapil 5', kursi_2029: 1, baseline: 23377, target: 34777, gap: 11400, kategori: 'Perjuangan' },
    { dapil: 'Dapil 6', kursi_2029: 1, baseline: 16114, target: 22414, gap: 6300, kategori: 'Perjuangan' },
  ],
  total_dapil: {
    kursi_2029: 9,
    baseline: 161304,
    target: 256204,
    gap: 94900,
  },
};

const formatDateIndo = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  // Sesuai aturan keamanan ketat: Hanya Super Admin yang berhak mengubah target suara atau baseline
  const isAuthorized = user?.role === 'super_admin';

  // Data Operasional Aktual dari API
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Data Target Kabupaten Tersimpan
  const [kabTargetItem, setKabTargetItem] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [savingTarget, setSavingTarget] = useState(false);
  const [targetForm, setTargetForm] = useState({
    target_suara_2029: 256204,
    target_rw_cakupan: 3228,
    baseline_suara_2024: 161304,
    target_kursi_2029: 9,
  });

  const [kecList, setKecList] = useState([]);
  const [partisipanList, setPartisipanList] = useState([]);
  const [lastUpdate, setLastUpdate] = useState('');

  // Pilihan tampilan bar chart kecamatan (Top 10 vs Semua)
  const [chartMode, setChartMode] = useState('top10');

  const loadData = () => {
    setStatsLoading(true);
    setStatsError(null);

    statsApi.summary()
      .then(res => {
        setStats(res.data);
      })
      .catch(err => {
        setStatsError(err?.response?.data?.detail || err.message);
      })
      .finally(() => setStatsLoading(false));

    // Muat Target Suara Kabupaten
    wilayahTargetApi.list({ level: 'kabupaten' })
      .then(res => {
        const items = res.data || [];
        if (items.length > 0) {
          const item = items[0];
          setKabTargetItem(item);
          setTargetForm({
            target_suara_2029: item.target_suara_2029 ?? 256204,
            target_rw_cakupan: item.target_rw_cakupan ?? 3228,
            baseline_suara_2024: item.baseline_suara_2024 ?? 161304,
            target_kursi_2029: item.target_kursi_2029 ?? 9,
          });
        }
      })
      .catch(() => {});

    statsApi.kecamatanDetail()
      .then(res => setKecList(res.data || []))
      .catch(() => setKecList([]));

    partisipanApi.list({ limit: 5 })
      .then(res => {
        const items = res.data?.items || res.data || [];
        setPartisipanList(Array.isArray(items) ? items : []);
      })
      .catch(() => setPartisipanList([]));

    setLastUpdate(new Date().toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenEdit = () => {
    if (!isAuthorized) {
      toast({
        title: "Akses Ditolak",
        description: "Hanya Super Admin yang berhak mengubah target.",
        variant: "destructive"
      });
      return;
    }
    setEditModalOpen(true);
  };

  const handleSaveTarget = async (e) => {
    e?.preventDefault();
    setSavingTarget(true);
    try {
      const payload = {
        level: 'kabupaten',
        target_suara_2029: parseInt(targetForm.target_suara_2029, 10) || 0,
        target_rw_cakupan: parseInt(targetForm.target_rw_cakupan, 10) || 0,
        baseline_suara_2024: parseInt(targetForm.baseline_suara_2024, 10) || 0,
        target_kursi_2029: parseInt(targetForm.target_kursi_2029, 10) || 9,
        catatan: 'Pengaturan Target Kabupaten dari Dashboard',
      };

      await wilayahTargetApi.upsert(payload);
      toast({
        title: "Target Berhasil Diperbarui",
        description: "Target Kabupaten Sukabumi berhasil disimpan ke database.",
      });
      setEditModalOpen(false);
      loadData();
    } catch (err) {
      toast({
        title: "Gagal Menyimpan",
        description: err.response?.data?.detail || err.message,
        variant: "destructive"
      });
    } finally {
      setSavingTarget(false);
    }
  };

  // Safe accessor helper untuk data operasional
  const getOpVal = (key, fallback = null) => {
    if (statsLoading) return '...';
    if (statsError) return 'Gagal memuat';
    if (!stats) return 'Belum tersedia';

    const parts = key.split('.');
    let cur = stats;
    for (const p of parts) {
      if (cur === null || cur === undefined) return fallback ?? 'Belum tersedia';
      cur = cur[p];
    }
    return cur !== undefined && cur !== null ? cur : (fallback ?? 'Belum tersedia');
  };

  const renderOpValue = (val) => {
    if (typeof val === 'number') return formatNumber(val);
    return val;
  };

  // Data Bar Chart Persebaran Orang per Kecamatan
  const sortedKec = [...kecList].sort((a, b) => (b.total_unik || 0) - (a.total_unik || 0));
  const chartData = chartMode === 'top10' ? sortedKec.slice(0, 10) : sortedKec;

  // Nilai Target Kabupaten: prioritaskan data kustom yang diinput admin, fallback ke master plan
  const activeTargetSuara = kabTargetItem?.target_suara_2029 ?? STRATEGIC_PLAN.indikator.target_suara_2029;
  const activeBaselineSuara = kabTargetItem?.baseline_suara_2024 ?? STRATEGIC_PLAN.indikator.baseline_suara_2024;
  const activeTargetRw = kabTargetItem?.target_rw_cakupan ?? STRATEGIC_PLAN.wilayah.rw;
  const activeTargetKursi = kabTargetItem?.target_kursi_2029 ?? STRATEGIC_PLAN.indikator.target_kursi;
  const activeGapSuara = Math.max(0, activeTargetSuara - activeBaselineSuara);

  // Nilai Cakupan RW Aktual
  const rwCovered = typeof stats?.rw?.value === 'number' ? stats.rw.value : 0;
  const rwTotal = activeTargetRw; // Menyesuaikan dengan target RW tersimpan
  const rwPct = rwTotal > 0 ? Math.round((rwCovered / rwTotal) * 100) : 0;
  const rwUncovered = Math.max(0, rwTotal - rwCovered);

  return (
    <div className="space-y-4 sm:space-y-5 max-w-[1600px] mx-auto pb-8">

      {/* ========================================================================= */}
      {/* 1. BANNER UTAMA (HERO SECTION SESUAI GAMBAR REFERENSI FINAL)               */}
      {/* ========================================================================= */}
      <div className="relative rounded-2xl overflow-hidden shadow-sm border border-orange-100 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 flex items-center p-4 sm:p-5">
        {/* Dekorasi Visual Lanskap Halus Pegunungan Sukabumi */}
        <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-orange-400 via-amber-300 to-transparent"></div>
        <div className="absolute right-0 bottom-0 top-0 w-1/3 opacity-15 pointer-events-none bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-orange-600 via-amber-500 to-transparent"></div>

        {/* Garis batas oranye di sisi kiri */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-orange-500 to-orange-600"></div>

        <div className="relative z-10 flex-1 flex flex-col md:flex-row md:items-center justify-between gap-3 pl-2">
          <div className="space-y-0.5">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
              <span>Peta Politik & Target Suara 2029</span>
            </h1>
            <h2 className="text-xs sm:text-sm font-bold text-orange-600 tracking-wide uppercase">
              DPD Kabupaten Sukabumi
            </h2>
            <p className="text-xs text-gray-500 font-medium pt-0.5">
              Ringkasan wilayah, target pemenangan, dan kekuatan organisasi.
            </p>
          </div>

          <div className="self-start md:self-center flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/90 backdrop-blur-sm border border-orange-200/80 shadow-xs text-xs font-semibold text-orange-700">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></span>
              <span>Periode 2024 – 2029</span>
            </div>
            {isAuthorized && (
              <Button
                size="sm"
                onClick={handleOpenEdit}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs h-8 px-3 gap-1.5 shadow-sm"
              >
                <Edit2 className="w-3.5 h-3.5" /> Atur Target
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 2. RINGKASAN WILAYAH (4 KARTU: DPT DULU, KEC, DESA, RW)                  */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Total DPT (Paling Depan) */}
        <div className="bg-white rounded-xl p-3 sm:p-3.5 card-shadow border border-gray-100 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0">
            <Vote className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 leading-tight tabular-nums tracking-tight whitespace-nowrap">
              {formatNumber(stats?.dpt_terupload && stats?.dpt_total > 0 ? stats.dpt_total : STRATEGIC_PLAN.wilayah.dpt_2024)}
            </div>
            <p className="text-[11px] sm:text-xs font-semibold text-gray-500 truncate">
              {stats?.dpt_terupload && stats?.dpt_total > 0 ? 'DPT Terunggah' : 'DPT 2024'}
            </p>
          </div>
        </div>

        {/* Kecamatan */}
        <div className="bg-white rounded-xl p-3 sm:p-3.5 card-shadow border border-gray-100 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-lg sm:text-xl font-bold text-gray-900 leading-tight tabular-nums tracking-tight">
              {formatNumber(stats?.kecamatan ?? STRATEGIC_PLAN.wilayah.kecamatan)}
            </div>
            <p className="text-[11px] sm:text-xs font-semibold text-gray-500 truncate">
              Kecamatan {stats?.dpt_terupload && <span className="text-[10px] text-emerald-600 font-bold">(DPT)</span>}
            </p>
          </div>
        </div>

        {/* Desa/Kelurahan */}
        <div className="bg-white rounded-xl p-3 sm:p-3.5 card-shadow border border-gray-100 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0">
            <Home className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-lg sm:text-xl font-bold text-gray-900 leading-tight tabular-nums tracking-tight">
              {formatNumber(stats?.desa ?? STRATEGIC_PLAN.wilayah.desa)}
            </div>
            <p className="text-[11px] sm:text-xs font-semibold text-gray-500 truncate">
              Desa/Kelurahan {stats?.dpt_terupload && <span className="text-[10px] text-emerald-600 font-bold">(DPT)</span>}
            </p>
          </div>
        </div>

        {/* Jumlah RW */}
        <div className="bg-white rounded-xl p-3 sm:p-3.5 card-shadow border border-gray-100 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-lg sm:text-xl font-bold text-gray-900 leading-tight tabular-nums tracking-tight">
              {formatNumber(stats?.rw?.total ?? STRATEGIC_PLAN.wilayah.rw)}
            </div>
            <p className="text-[11px] sm:text-xs font-semibold text-gray-500 truncate">
              Jumlah RW
            </p>
            {stats?.rw?.total_dpt ? (
              <p className="text-[10px] text-gray-400 font-medium leading-tight truncate" title={`${formatNumber(stats.rw.total_dpt)} data RW dari DPT`}>
                {formatNumber(stats.rw.total_dpt)} data RW dari DPT
                {stats?.rw?.total && stats.rw.total_dpt !== stats.rw.total && (
                  <span className="text-rose-500 font-bold ml-1">
                    (selisih {formatNumber(stats.rw.total - stats.rw.total_dpt)})
                  </span>
                )}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3. LIMA KARTU STRATEGIS (TARGET KURSI, TARGET RW, BASELINE, TARGET, GAP) */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        {/* Kartu 1: Target Kursi 2029 */}
        <div className="bg-gradient-to-br from-orange-50/70 to-amber-50/50 rounded-xl p-3.5 sm:p-4 border border-orange-200/80 card-shadow flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
                  <Award className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Target Kursi 2029</span>
              </div>
              {isAuthorized && (
                <button onClick={handleOpenEdit} className="text-orange-600/70 hover:text-orange-700 p-1 rounded hover:bg-orange-100/50" title="Ubah Target">
                  <Edit2 className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-orange-600 mt-1 leading-tight tabular-nums tracking-tight">
              {activeTargetKursi}
            </div>
          </div>
          <p className="text-xs font-medium text-orange-700/80 mt-1">Kursi DPRD</p>
        </div>

        {/* Kartu 2: Target RW */}
        <div className="bg-gradient-to-br from-emerald-50/70 to-green-50/50 rounded-xl p-3.5 sm:p-4 border border-emerald-200/80 card-shadow flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <TargetIcon className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Target RW</span>
              </div>
              {isAuthorized && (
                <button onClick={handleOpenEdit} className="text-emerald-700/70 hover:text-emerald-800 p-1 rounded hover:bg-emerald-100/50" title="Ubah Target RW">
                  <Edit2 className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-emerald-800 mt-1 leading-tight tabular-nums tracking-tight">
              {formatNumber(activeTargetRw)}
            </div>
          </div>
          <p className="text-xs font-medium text-emerald-700/80 mt-1">Sasaran RW Terpilih</p>
        </div>

        {/* Kartu 3: Baseline Suara 2024 */}
        <div className="bg-gradient-to-br from-blue-50/70 to-indigo-50/50 rounded-xl p-3.5 sm:p-4 border border-blue-200/80 card-shadow flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                  <Flag className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Baseline Suara 2024</span>
              </div>
              {isAuthorized && (
                <button onClick={handleOpenEdit} className="text-blue-600/70 hover:text-blue-700 p-1 rounded hover:bg-blue-100/50" title="Ubah Target">
                  <Edit2 className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-blue-900 mt-1 leading-tight tabular-nums tracking-tight">
              {formatNumber(activeBaselineSuara)}
            </div>
          </div>
          <p className="text-xs font-medium text-blue-600 mt-1">Suara Resmi 2024</p>
        </div>

        {/* Kartu 4: Target Suara 2029 */}
        <div className="bg-gradient-to-br from-teal-50/70 to-cyan-50/50 rounded-xl p-3.5 sm:p-4 border border-teal-200/80 card-shadow flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center">
                  <TargetIcon className="w-3.5 h-3.5" />
                </div>
                <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Target Suara 2029</span>
              </div>
              {isAuthorized && (
                <button onClick={handleOpenEdit} className="text-teal-700/70 hover:text-teal-800 p-1 rounded hover:bg-teal-100/50" title="Ubah Target">
                  <Edit2 className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-teal-900 mt-1 leading-tight tabular-nums tracking-tight">
              {formatNumber(activeTargetSuara)}
            </div>
          </div>
          <p className="text-xs font-medium text-teal-700 mt-1">Sasaran Suara</p>
        </div>

        {/* Kartu 5: Gap Suara */}
        <div className="bg-gradient-to-br from-rose-50/70 to-red-50/50 rounded-xl p-3.5 sm:p-4 border border-rose-200/80 card-shadow flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center">
                <TrendingUp className="w-3.5 h-3.5" />
              </div>
              <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Gap Suara</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-rose-600 mt-1 leading-tight tabular-nums tracking-tight">
              +{formatNumber(activeGapSuara)}
            </div>
          </div>
          <p className="text-xs font-medium text-rose-600 mt-1">Kekurangan Suara</p>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 4 & 5. DUA KOLOM: TABEL TARGET DAPIL & PANEL PETA DAPIL                   */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">

        {/* KOLOM KIRI: TABEL TARGET SUARA PER DAPIL (7 COLS) */}
        <div className="lg:col-span-7 bg-white rounded-2xl p-4 sm:p-5 card-shadow border border-gray-100 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-black text-gray-900">
                Target Suara per Dapil
              </h3>
              <span className="text-xs font-semibold text-gray-400">6 Daerah Pemilihan</span>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50/80 text-gray-600 text-xs">
                    <TableHead className="font-extrabold text-gray-900 py-2">Dapil</TableHead>
                    <TableHead className="font-extrabold text-right text-gray-900 py-2">Kursi 2029</TableHead>
                    <TableHead className="font-extrabold text-right text-gray-900 py-2">Baseline 2024</TableHead>
                    <TableHead className="font-extrabold text-right text-gray-900 py-2">Target 2029</TableHead>
                    <TableHead className="font-extrabold text-right text-gray-900 py-2">Gap Suara</TableHead>
                    <TableHead className="font-extrabold text-center text-gray-900 py-2">Kategori</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {STRATEGIC_PLAN.dapil_table.map((row) => (
                    <TableRow key={row.dapil} className="hover:bg-orange-50/30 transition-colors text-xs">
                      <TableCell className="font-extrabold text-gray-900 py-2">
                        {row.dapil}
                      </TableCell>
                      <TableCell className="text-right font-black text-gray-900 py-2">
                        {row.kursi_2029}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-600 py-2">
                        {formatNumber(row.baseline)}
                      </TableCell>
                      <TableCell className="text-right font-extrabold text-gray-900 py-2">
                        {formatNumber(row.target)}
                      </TableCell>
                      <TableCell className="text-right font-black text-rose-600 py-2">
                        +{formatNumber(row.gap)}
                      </TableCell>
                      <TableCell className="text-center py-2">
                        <Badge
                          variant="outline"
                          className={
                            row.kategori === 'Potensial'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 font-extrabold text-[11px] py-0'
                              : row.kategori === 'Menantang'
                              ? 'bg-blue-50 text-blue-700 border-blue-200 font-extrabold text-[11px] py-0'
                              : 'bg-amber-50 text-amber-700 border-amber-200 font-bold text-[11px] py-0'
                          }
                        >
                          {row.kategori}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* BARIS TOTAL */}
                  <TableRow className="bg-orange-50/60 border-t-2 border-orange-200 font-black text-gray-950 text-xs">
                    <TableCell className="font-black uppercase py-2.5">
                      Total
                    </TableCell>
                    <TableCell className="text-right font-black text-sm text-gray-950 py-2.5">
                      {STRATEGIC_PLAN.total_dapil.kursi_2029}
                    </TableCell>
                    <TableCell className="text-right font-black text-xs text-gray-900 py-2.5">
                      {formatNumber(STRATEGIC_PLAN.total_dapil.baseline)}
                    </TableCell>
                    <TableCell className="text-right font-black text-xs text-gray-950 py-2.5">
                      {formatNumber(STRATEGIC_PLAN.total_dapil.target)}
                    </TableCell>
                    <TableCell className="text-right font-black text-sm text-rose-600 py-2.5">
                      +{formatNumber(STRATEGIC_PLAN.total_dapil.gap)}
                    </TableCell>
                    <TableCell className="text-center font-bold text-gray-400 py-2.5">—</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400">
            <span>* Sumber: KPU (Pileg 2024); Dokumen Perencanaan BP3 DPD PKS Kab. Sukabumi</span>
            <Button variant="link" size="sm" onClick={() => navigate('/target-suara')} className="text-xs font-bold text-orange-600 p-0 h-auto">
              Lihat Detail Hierarki →
            </Button>
          </div>
        </div>

        {/* KOLOM KANAN: PETA DAPIL KABUPATEN SUKABUMI (5 COLS) */}
        <div className="lg:col-span-5 bg-white rounded-2xl p-4 sm:p-5 card-shadow border border-gray-100 flex flex-col justify-between">
          <div>
            <h3 className="text-base font-black text-gray-900 mb-0.5">
              Peta Dapil Kabupaten Sukabumi
            </h3>
            <p className="text-xs text-gray-500 font-medium mb-3">
              Pembagian 6 Daerah Pemilihan beserta alokasi target kursi.
            </p>

            {/* Peta Interaktif OpenStreetMap 6 Dapil Sukabumi */}
            <div className="mt-2">
              <PetaDapilLeaflet height={240} showControls={true} showLegend={true} />
            </div>

            {/* Panel Ambang Target Kursi Tambahan (Fokus Utama Dapil 3 & 4) */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs font-bold text-gray-900 mb-2">
                <Award className="w-3.5 h-3.5 text-amber-500" />
                <span>Ambang Kursi Tambahan (Fokus Utama)</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Box Dapil 3 */}
                <div className="p-2.5 rounded-xl bg-blue-50/80 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-blue-900">Dapil 3 (Cikembar dsk)</p>
                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-blue-200 text-blue-800">2 Kursi ⭐</span>
                  </div>
                  <p className="text-sm font-black text-blue-700 mt-1">&gt; 50.938 suara</p>
                  <p className="text-[10px] text-blue-600 font-semibold mt-0.5">Target: 58.764 suara</p>
                </div>

                {/* Box Dapil 4 */}
                <div className="p-2.5 rounded-xl bg-emerald-50/80 border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-emerald-900">Dapil 4 (Cisaat dsk)</p>
                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-800">3 Kursi ⭐⭐</span>
                  </div>
                  <p className="text-sm font-black text-emerald-700 mt-1">&gt; 78.106 suara</p>
                  <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">Target: 85.338 suara</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-[10px] text-gray-400 mt-3 pt-2 border-t border-gray-100">
            Penambahan 2 kursi difokuskan pada Dapil 3 (Cikembar dsk) dan Dapil 4 (Cisaat dsk).
          </p>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 6. STATISTIK KEKUATAN ORGANISASI & CAKUPAN LAPANGAN (WAJIB DIPERTAHANKAN) */}
      {/* ========================================================================= */}
      <div className="bg-white rounded-2xl p-4 sm:p-5 card-shadow border border-gray-100 space-y-3.5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 border-b border-gray-100 pb-3">
          <div>
            <div className="flex items-center gap-2">
              <Users className="w-4.5 h-4.5 text-orange-600" />
              <h2 className="text-base sm:text-lg font-black text-gray-900">
                Kekuatan Organisasi & Cakupan Lapangan
              </h2>
            </div>
            <p className="text-xs text-gray-500 font-medium mt-0.5">
              Data aktual dari sistem pendataan. Satu orang dapat memiliki lebih dari satu peran.
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <span className="text-[11px] text-gray-400 font-medium hidden sm:inline">
              Terakhir diperbarui: {lastUpdate || 'Hari ini'}
            </span>
            <Button variant="outline" size="sm" onClick={loadData} className="gap-1 text-xs font-bold border-gray-200 h-7.5 px-2.5">
              <RefreshCcw className={`w-3 h-3 ${statsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {statsError && (
          <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />
            <span>Gagal memuat statistik organisasi: {statsError}</span>
          </div>
        )}

        {/* 9 KARTU KEKUATAN ORGANISASI SESUAI GAMBAR REFERENSI */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-9 gap-2.5">
          {/* 1. Total Orang Unik */}
          <div className="p-3 rounded-xl bg-orange-50/70 border border-orange-200">
            <div className="flex items-center gap-1.5 text-orange-700 mb-0.5">
              <Users className="w-3.5 h-3.5" />
              <span className="text-[10px] font-extrabold uppercase">Total Orang</span>
            </div>
            <div className="text-xl font-black text-gray-950 mt-0.5">
              {renderOpValue(getOpVal('total_orang', 0))}
            </div>
            <p className="text-[10px] text-orange-600 font-semibold">orang</p>
          </div>

          {/* 2. Kader Pelopor */}
          <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-200">
            <div className="flex items-center gap-1.5 text-emerald-700 mb-0.5">
              <UserCheck className="w-3.5 h-3.5" />
              <span className="text-[10px] font-extrabold uppercase">Kader Pelopor</span>
            </div>
            <div className="text-xl font-black text-gray-950 mt-0.5">
              {renderOpValue(getOpVal('pelopor_peran', 0))}
            </div>
            <p className="text-[10px] text-emerald-600 font-semibold">orang</p>
          </div>

          {/* 3. Kader Non-Pelopor */}
          <div className="p-3 rounded-xl bg-blue-50/70 border border-blue-200">
            <div className="flex items-center gap-1.5 text-blue-700 mb-0.5">
              <Users className="w-3.5 h-3.5" />
              <span className="text-[10px] font-extrabold uppercase">Non-Pelopor</span>
            </div>
            <div className="text-xl font-black text-gray-950 mt-0.5">
              {renderOpValue(getOpVal('non_pelopor_peran', 0))}
            </div>
            <p className="text-[10px] text-blue-600 font-semibold">orang</p>
          </div>

          {/* 4. Saksi */}
          <div className="p-3 rounded-xl bg-rose-50/70 border border-rose-200">
            <div className="flex items-center gap-1.5 text-rose-700 mb-0.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="text-[10px] font-extrabold uppercase">Saksi</span>
            </div>
            <div className="text-xl font-black text-gray-950 mt-0.5">
              {renderOpValue(getOpVal('saksi.value', 0))}
            </div>
            <p className="text-[10px] text-rose-600 font-semibold">orang</p>
          </div>

          {/* 5. Relawan / Simpatisan */}
          <div className="p-3 rounded-xl bg-purple-50/70 border border-purple-200">
            <div className="flex items-center gap-1.5 text-purple-700 mb-0.5">
              <Award className="w-3.5 h-3.5" />
              <span className="text-[10px] font-extrabold uppercase">Relawan</span>
            </div>
            <div className="text-xl font-black text-gray-950 mt-0.5">
              {renderOpValue(getOpVal('simpatisan.value', 0))}
            </div>
            <p className="text-[10px] text-purple-600 font-semibold">orang</p>
          </div>

          {/* 6. Kader RKI */}
          <div className="p-3 rounded-xl bg-amber-50/70 border border-amber-200">
            <div className="flex items-center gap-1.5 text-amber-700 mb-0.5">
              <Award className="w-3.5 h-3.5" />
              <span className="text-[10px] font-extrabold uppercase">Kader RKI</span>
            </div>
            <div className="text-xl font-black text-gray-950 mt-0.5">
              {renderOpValue(getOpVal('rki', 0))}
            </div>
            <p className="text-[10px] text-amber-600 font-semibold">orang</p>
          </div>

          {/* 7. Pengurus DPC */}
          <div className="p-3 rounded-xl bg-teal-50/70 border border-teal-200">
            <div className="flex items-center gap-1.5 text-teal-700 mb-0.5">
              <Building2 className="w-3.5 h-3.5" />
              <span className="text-[10px] font-extrabold uppercase">Pengurus DPC</span>
            </div>
            <div className="text-xl font-black text-gray-950 mt-0.5">
              {renderOpValue(getOpVal('pengurus_dpc', 0))}
            </div>
            <p className="text-[10px] text-teal-600 font-semibold">orang</p>
          </div>

          {/* 8. Pengurus DPRa */}
          <div className="p-3 rounded-xl bg-indigo-50/70 border border-indigo-200">
            <div className="flex items-center gap-1.5 text-indigo-700 mb-0.5">
              <Home className="w-3.5 h-3.5" />
              <span className="text-[10px] font-extrabold uppercase">Pengurus DPRa</span>
            </div>
            <div className="text-xl font-black text-gray-950 mt-0.5">
              {renderOpValue(getOpVal('pengurus_dpra', 0))}
            </div>
            <p className="text-[10px] text-indigo-600 font-semibold">orang</p>
          </div>

          {/* 9. Tokoh */}
          <div className="p-3 rounded-xl bg-slate-100 border border-slate-200 col-span-2 sm:col-span-1">
            <div className="flex items-center gap-1.5 text-slate-700 mb-0.5">
              <Users className="w-3.5 h-3.5" />
              <span className="text-[10px] font-extrabold uppercase">Tokoh</span>
            </div>
            <div className="text-xl font-black text-gray-950 mt-0.5">
              {renderOpValue(getOpVal('tokoh', 0))}
            </div>
            <p className="text-[10px] text-slate-600 font-semibold">orang</p>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 7. TIGA PANEL BAWAH: PERSEBARAN KECAMATAN, CAKUPAN RW, DAFTAR TERBARU     */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">

        {/* PANEL A: PERSEBARAN ORANG PER KECAMATAN (5 COLS) */}
        <div className="lg:col-span-5 bg-white rounded-2xl p-4 sm:p-5 card-shadow border border-gray-100 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm sm:text-base font-black text-gray-900">
                Persebaran Orang per Kecamatan
              </h3>
              <select
                value={chartMode}
                onChange={(e) => setChartMode(e.target.value)}
                className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 font-semibold text-gray-700 focus:outline-none"
              >
                <option value="top10">Top 10 Kecamatan</option>
                <option value="all">Semua Kecamatan</option>
              </select>
            </div>

            <div className="h-56 w-full">
              {chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-gray-400 font-medium">
                  Belum ada data orang di kecamatan
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: -22, bottom: 35 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                    <XAxis
                      dataKey="kecamatan"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={45}
                      fontSize={9.5}
                      stroke="#9CA3AF"
                    />
                    <YAxis fontSize={9.5} stroke="#9CA3AF" allowDecimals={false} />
                    <Tooltip
                      formatter={(v) => [`${formatNumber(v)} orang`, 'Total']}
                      contentStyle={{ borderRadius: 8, border: '1px solid #FED7AA', fontSize: 11 }}
                    />
                    <Bar dataKey="total_unik" fill="#F97316" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5 text-center">
            Statistik orang unik terdata berdasarkan wilayah kecamatan.
          </p>
        </div>

        {/* PANEL B: CAKUPAN RW (3 COLS) */}
        <div className="lg:col-span-3 bg-white rounded-2xl p-4 sm:p-5 card-shadow border border-gray-100 flex flex-col justify-between">
          <div>
            <h3 className="text-sm sm:text-base font-black text-gray-900 mb-3">
              Cakupan RW
            </h3>

            {/* Gauge Donut Lingkaran Persentase */}
            <div className="flex flex-col items-center justify-center my-2">
              <div className="relative w-24 h-24 flex items-center justify-center">
                <svg className="w-24 h-24 -rotate-90">
                  <circle cx="48" cy="48" r="38" stroke="#F3F4F6" strokeWidth="7" fill="none" />
                  <circle
                    cx="48" cy="48" r="38"
                    stroke="#10B981"
                    strokeWidth="7"
                    fill="none"
                    strokeDasharray={239}
                    strokeDashoffset={239 - (239 * Math.min(100, rwPct)) / 100}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute text-center">
                  <span className="text-xl font-black text-gray-950">{rwPct}%</span>
                </div>
              </div>
              <p className="text-xs font-black text-gray-800 mt-1.5">
                {formatNumber(rwCovered)} / {formatNumber(rwTotal)} RW
              </p>
            </div>

            {/* Rincian Indikator RW */}
            <div className="space-y-1.5 mt-3 text-xs">
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <span className="text-gray-500 font-medium">RW ada orang</span>
                <span className="font-extrabold text-gray-900">{formatNumber(rwCovered)}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <span className="text-gray-500 font-medium">RW belum ada data</span>
                <span className="font-extrabold text-rose-600">{formatNumber(rwUncovered)}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <span className="text-gray-500 font-medium">Target min Dapil 3&4</span>
                <span className="font-extrabold text-orange-600">1.007</span>
              </div>
            </div>
          </div>

          <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between text-xs">
            <span className="text-gray-400 font-medium">Status</span>
            <span className="inline-flex items-center gap-1.5 font-bold text-orange-600">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></span>
              Dalam proses
            </span>
          </div>
        </div>

        {/* PANEL C: DAFTAR NAMA TERBARU (4 COLS) */}
        <div className="lg:col-span-4 bg-white rounded-2xl p-4 sm:p-5 card-shadow border border-gray-100 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm sm:text-base font-black text-gray-900">
                Daftar Nama Terbaru
              </h3>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/partisipan')}
                className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-7 px-2.5 font-bold rounded-lg"
              >
                Lihat Semua
              </Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-100 text-[11px] text-gray-500">
                    <TableHead className="py-1.5 px-1 font-bold">Nama</TableHead>
                    <TableHead className="py-1.5 px-1 font-bold">Kategori</TableHead>
                    <TableHead className="py-1.5 px-1 font-bold">Wilayah</TableHead>
                    <TableHead className="py-1.5 px-1 font-bold text-right">Tanggal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partisipanList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-6 text-gray-400 text-xs font-medium">
                        Belum ada profil terdaftar di sistem
                      </TableCell>
                    </TableRow>
                  ) : (
                    partisipanList.map((p) => (
                      <TableRow key={p.id} className="text-xs hover:bg-orange-50/40">
                        <TableCell className="py-2 px-1 font-extrabold text-gray-900 truncate max-w-[100px]">
                          {p.nama}
                        </TableCell>
                        <TableCell className="py-2 px-1 font-semibold text-orange-600 truncate max-w-[90px]">
                          {p.peran === 'pelopor' ? 'Kader Pelopor' : p.peran === 'non_pelopor' ? 'Kader Non-Pelopor' : p.peran === 'saksi' ? 'Saksi' : 'Relawan'}
                        </TableCell>
                        <TableCell className="py-2 px-1 text-gray-600 truncate max-w-[80px]">
                          {p.kecamatan || '-'}
                        </TableCell>
                        <TableCell className="py-2 px-1 text-right text-gray-400 font-medium">
                          {formatDateIndo(p.tanggal)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <p className="text-[10px] text-gray-400 mt-2 text-center">
            Menampilkan 5 data orang terakhir yang diinput ke dalam sistem.
          </p>
        </div>

      </div>

      {/* ========================================================================= */}
      {/* 5. MODAL EDIT TARGET TINGKAT KABUPATEN                                    */}
      {/* ========================================================================= */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-orange-50/80 to-amber-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center font-bold">
                  <TargetIcon className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-black text-gray-900">Atur Target Kabupaten</h3>
                  <p className="text-[11px] text-gray-500 font-medium">Kabupaten Sukabumi (Tingkat Makro)</p>
                </div>
              </div>
              <button
                onClick={() => setEditModalOpen(false)}
                className="w-7 h-7 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-white flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveTarget} className="p-4 sm:p-5 space-y-3.5">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Target Suara 2029 <span className="text-rose-500">*</span>
                </label>
                <Input
                  type="number"
                  min={0}
                  value={targetForm.target_suara_2029}
                  onChange={e => setTargetForm({ ...targetForm, target_suara_2029: e.target.value })}
                  placeholder="256204"
                  className="font-black text-orange-600 text-base"
                  required
                />
                <p className="text-[10px] text-gray-400 mt-1">Default master plan: 256.204 suara</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Target RW <span className="text-rose-500">*</span>
                </label>
                <Input
                  type="number"
                  min={0}
                  value={targetForm.target_rw_cakupan}
                  onChange={e => setTargetForm({ ...targetForm, target_rw_cakupan: e.target.value })}
                  placeholder="3228"
                  className="font-bold text-gray-900"
                  required
                />
                <p className="text-[10px] text-gray-400 mt-1">Default master wilayah: 3.228 RW</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Baseline 2024
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={targetForm.baseline_suara_2024}
                    onChange={e => setTargetForm({ ...targetForm, baseline_suara_2024: e.target.value })}
                    placeholder="161304"
                    className="font-medium text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Target Kursi DPRD
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={targetForm.target_kursi_2029}
                    onChange={e => setTargetForm({ ...targetForm, target_kursi_2029: e.target.value })}
                    placeholder="9"
                    className="font-bold text-orange-600"
                  />
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end gap-2 border-t border-gray-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditModalOpen(false)}
                  className="text-xs h-8 font-semibold"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  disabled={savingTarget}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs h-8 gap-1.5 shadow-sm"
                >
                  <Save className="w-3.5 h-3.5" />
                  {savingTarget ? 'Menyimpan...' : 'Simpan Target'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;

